import React, { useState, useEffect, useMemo } from 'react';
import SelectField from '@/components/atoms/SelectField';
import { Control, FieldErrors, UseFormSetValue, UseFormWatch, UseFormRegister, useWatch } from 'react-hook-form';
import AllocationBar from '@/components/organisms/AllocationBar';
import InputField from '@/components/atoms/InputField';
import euroIcon from '@/assets/icons/euro.svg';
import toast from 'react-hot-toast';
import { BankAccount } from '@/services/types/bank-accounts.types';
import { AccountingCenter } from '@/services/types/accounting-centers-types';
import { ExpenseCategory } from '@/services/types/expense-category.types';
import { RevenueCategory } from '@/services/types/revenue-category.types';

interface CreateInvoiceAllocationProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  control: Control<any>;
  budgetId: string;
  bankAccountsData: BankAccount[];
  accountingCentersData: AccountingCenter[];
  invoiceCategoriesData: ExpenseCategory[] | RevenueCategory[];
}

// Helper function to convert hex to HSL
const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const r = parseInt(hex.substring(1, 3), 16) / 255;
  const g = parseInt(hex.substring(3, 5), 16) / 255;
  const b = parseInt(hex.substring(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

// Helper function to convert HSL to hex
const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  const r = Math.round(255 * f(0));
  const g = Math.round(255 * f(8));
  const b = Math.round(255 * f(4));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

// Helper function to generate colors for items
const generateColorForIndex = (index: number): string => {
  const baseColors = ['#7663EB', '#48C80C'];

  // Return predefined colors for first two items
  if (index < baseColors.length) {
    return baseColors[index];
  }

  // For 3rd and onward items, generate random colors with similar brightness
  const hsl1 = hexToHSL(baseColors[0]);
  const hsl2 = hexToHSL(baseColors[1]);

  // Calculate average lightness and saturation
  const avgLightness = (hsl1.l + hsl2.l) / 2;
  const avgSaturation = (hsl1.s + hsl2.s) / 2;

  // Generate random hue but keep similar lightness and saturation with some variation
  const randomHue = Math.floor(Math.random() * 360);
  const lightnessVariation = (Math.random() - 0.5) * 15; // ±7.5% variation
  const saturationVariation = (Math.random() - 0.5) * 20; // ±10% variation

  const newLightness = Math.max(30, Math.min(70, avgLightness + lightnessVariation));
  const newSaturation = Math.max(40, Math.min(100, avgSaturation + saturationVariation));

  return hslToHex(randomHue, newSaturation, newLightness);
};

const CreateInvoiceAllocation = ({
  register,
  errors,
  watch,
  setValue,
  control,
  budgetId,
  bankAccountsData,
  accountingCentersData,
  invoiceCategoriesData
}: CreateInvoiceAllocationProps) => {
  const allocationAmount = useWatch({ control, name: 'payment_amount' });
  const invoiceCategory = useWatch({ control, name: 'invoice_category' });

  // Watch only the selection arrays so we know which dynamic ID fields exist.
  const bankAccountIds: string[] = useWatch({ control, name: 'bank_account' }) || [];
  const departmentIds: string[] = useWatch({ control, name: 'department_accounting_center' }) || [];
  const productServicesIds: string[] = useWatch({ control, name: 'product_and_services_accounting_center' }) || [];
  const projectIds: string[] = useWatch({ control, name: 'project_accounting_center' }) || [];
  const contractIds: string[] = useWatch({ control, name: 'contract_accounting_center' }) || [];
  const invoiceCategoryIds: string[] = useWatch({ control, name: 'invoice_categories' }) || [];

  // Subscribe to all dynamic allocation amount fields in one shot. `useWatch` will
  // re-render this component when any of them change.
  const allDynamicIds = useMemo(
    () => [
      ...bankAccountIds,
      ...departmentIds,
      ...productServicesIds,
      ...projectIds,
      ...contractIds,
      ...invoiceCategoryIds
    ],
    [bankAccountIds, departmentIds, productServicesIds, projectIds, contractIds, invoiceCategoryIds]
  );
  const dynamicAmounts: any[] = (useWatch({ control, name: allDynamicIds }) as any[]) || [];

  const amountByIdRef = useMemo(() => {
    const map: Record<string, number> = {};
    allDynamicIds.forEach((id, i) => {
      map[id] = parseFloat(dynamicAmounts[i]) || 0;
    });
    return map;
  }, [allDynamicIds, dynamicAmounts]);

  const sumIds = (ids: string[]) => ids.reduce((sum, id) => sum + (amountByIdRef[id] || 0), 0);
  const numericAllocationAmount = Number(allocationAmount) || 0;

  const isEqual = {
    bankAccounts: sumIds(bankAccountIds) === numericAllocationAmount,
    departmentsAC: sumIds(departmentIds) === numericAllocationAmount,
    productsAndServicesAC: sumIds(productServicesIds) === numericAllocationAmount,
    projectAC: sumIds(projectIds) === numericAllocationAmount,
    contractAC: sumIds(contractIds) === numericAllocationAmount,
    invoiceCategory: sumIds(invoiceCategoryIds) === numericAllocationAmount
  };

  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [touchedSections, setTouchedSections] = useState<Set<string>>(new Set());

  const handleFieldBlur = (fieldName: string, section: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName));
    setTouchedSections((prev) => new Set(prev).add(section));
  };

  console.log('invoiceCategory', invoiceCategory);

  const [bankAccountsOptions, setBankAccountsOptions] = useState<
    { value: string; label: React.ReactNode; title?: string; color?: string; remainingBalance?: number }[]
  >([]);
  const [accountingCentersOptions, setAccountingCentersOptions] = useState<{
    department: { value: string; label: React.ReactNode; title?: string; color?: string; max?: number }[];
    productAndServices: { value: string; label: React.ReactNode; title?: string; color?: string; max?: number }[];
    project: { value: string; label: React.ReactNode; title?: string; color?: string; max?: number }[];
    contract: { value: string; label: React.ReactNode; title?: string; color?: string; max?: number }[];
  }>({
    department: [],
    productAndServices: [],
    project: [],
    contract: []
  });
  const [invoiceCategoriesOptions, setInvoiceCategoriesOptions] = useState<
    { value: string; label: React.ReactNode; title?: string; color?: string; max?: number }[]
  >([]);

  const generateAllocationOptions = (arr: any[], type: string, label: string = 'Remaining Budget') => {
    return arr.map((center, index) => ({
      value: center._id,
      label: (
        <div>
          <p className="font-medium">{center?.name || center?.accountName || '-'}</p>
          <p className="text-sm font-medium text-secondary">
            {label} €{center[type]}
          </p>
        </div>
      ),
      title: center?.name || center?.accountName || '-',
      color: generateColorForIndex(index),
      ...(type === 'remainingBalance' ? { remainingBalance: center.remainingBalance ?? 0 } : {})
    }));
  };

  const insufficientBankAccounts = bankAccountIds
    .map((id) => {
      const bank = bankAccountsData.find((account) => account._id === id);
      const allocatedAmount = amountByIdRef[id] || 0;
      const remainingBalance = bank?.remainingBalance ?? 0;

      if (allocatedAmount > remainingBalance) {
        return {
          id,
          name: bank?.accountName || 'Bank account',
          remainingBalance
        };
      }

      return null;
    })
    .filter((item): item is { id: string; name: string; remainingBalance: number } => item !== null);

  useEffect(() => {
    if (bankAccountsData) {
      const formattedBankAccounts = generateAllocationOptions(
        bankAccountsData,
        'remainingBalance',
        'Remaining Balance'
      );
      setBankAccountsOptions(formattedBankAccounts);
    }

    if (accountingCentersData) {
      const type = invoiceCategory === 'expense' ? 'acmev' : 'acrt_achieved';
      const label = invoiceCategory === 'expense' ? 'Remaining Budget' : 'Acheived Revenue';

      const formattedDepartmentOptions = generateAllocationOptions(
        accountingCentersData.filter((ac) => ac.type === 'DEPARTMENT_ACCOUNTING'),
        type,
        label
      );
      const formattedContractOptions = generateAllocationOptions(
        accountingCentersData.filter((ac) => ac.type === 'CONTRACTS'),
        type,
        label
      );
      const formattedProjectOptions = generateAllocationOptions(
        accountingCentersData.filter((ac) => ac.type === 'PROJECTS'),
        type,
        label
      );
      const formattedProductsAndServicesOptions = generateAllocationOptions(
        accountingCentersData.filter((ac) => ac.type === 'PRODUCTS_AND_SERVICES'),
        type,
        label
      );

      setAccountingCentersOptions({
        department: formattedDepartmentOptions,
        contract: formattedContractOptions,
        project: formattedProjectOptions,
        productAndServices: formattedProductsAndServicesOptions
      });
    }

    if (invoiceCategoriesData) {
      const type = invoiceCategory === 'expense' ? 'ecmev' : 'rcrt_achieved';
      const label = invoiceCategory === 'expense' ? 'Remaining Budget' : 'Acheived Revenue';
      const formattedInvoiceCategories = generateAllocationOptions(invoiceCategoriesData, type, label);
      setInvoiceCategoriesOptions(formattedInvoiceCategories);
    }
  }, [bankAccountsData, accountingCentersData, invoiceCategoriesData]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card space-y-4">
        <p className="font-medium text-base">Bank Account</p>
        <SelectField
          name="bank_account"
          placeholder="Select bank account"
          options={bankAccountsOptions}
          register={register}
          value={watch('bank_account')}
          error={errors.bank_account}
          size="sm"
          multiple
          title="bank accounts"
        />

        {watch('bank_account') && Array.isArray(watch('bank_account')) && watch('bank_account').length > 0 && (
          <div className="space-y-4">
            <AllocationBar
              items={(watch('bank_account') || []).map((bankAccount: any) => {
                const bankAccountData = bankAccountsOptions.find((account: any) => account.value === bankAccount);
                return {
                  value: watch(bankAccount) || 0,
                  label: '',
                  color: bankAccountData?.color || ''
                };
              })}
              maxAllowedTotal={allocationAmount}
              height={22}
              borderRadius={12}
              isOverAllocated={
                touchedSections.has('bankAccounts') && (!isEqual.bankAccounts || insufficientBankAccounts.length > 0)
              }
            />

            {(watch('bank_account') || []).map((bankAccount: any) => {
              const bankAccountData = bankAccountsOptions.find((account: any) => account.value === bankAccount);
              return (
                <div key={bankAccount} className="flex items-center justify-between gap-4">
                  <div className="flex-1 flex items-center gap-2">
                    <span className="w-[9px] h-[9px] rounded-full" style={{ background: bankAccountData?.color }} />
                    <p className="flex-1 text-sm font-medium leading-[100%]">{bankAccountData?.title || ''}</p>
                  </div>
                  <InputField
                    name={bankAccount}
                    placeholder="Amount"
                    register={register}
                    error={touchedFields.has(bankAccount) ? errors[bankAccount] : undefined}
                    onBlur={() => handleFieldBlur(bankAccount, 'bankAccounts')}
                    size="sm"
                    type="number"
                    className="w-[125px]"
                    icon={euroIcon}
                    min={0}
                  />
                </div>
              );
            })}

            {touchedSections.has('bankAccounts') &&
              insufficientBankAccounts.map((bank) => (
                <div key={bank.id} className="text-red-500 text-sm">
                  {bank.name} has insufficient funds (Remaining Balance €{bank.remainingBalance.toLocaleString()})
                </div>
              ))}

            {touchedSections.has('bankAccounts') && !isEqual.bankAccounts && (
              <div className="text-red-500 text-sm">
                Invoice allocation must have a value equal to the Invoice amount (€{allocationAmount})
              </div>
            )}
          </div>
        )}
      </div>

      {accountingCentersOptions.department.length > 0 && (
        <div className="card space-y-4">
          <p className="font-medium text-base">Department Accounting Center</p>
          <SelectField
            name="department_accounting_center"
            placeholder="Select accounting center"
            options={accountingCentersOptions.department}
            register={register}
            value={watch('department_accounting_center')}
            error={errors.department_accounting_center}
            size="sm"
            multiple
            title="accounting centers"
          />

          {watch('department_accounting_center') &&
            Array.isArray(watch('department_accounting_center')) &&
            watch('department_accounting_center').length > 0 && (
              <div className="space-y-4">
                <AllocationBar
                  items={(watch('department_accounting_center') || []).map((centerId: any) => {
                    const centerData = accountingCentersOptions.department.find(
                      (center: any) => center.value === centerId
                    );
                    return {
                      value: watch(`${centerId}`) || 0,
                      label: '',
                      color: centerData?.color || ''
                    };
                  })}
                  maxAllowedTotal={allocationAmount}
                  height={22}
                  borderRadius={12}
                  isOverAllocated={touchedSections.has('departmentsAC') && !isEqual.departmentsAC}
                />

                {(watch('department_accounting_center') || []).map((centerId: any) => {
                  const centerData = accountingCentersOptions.department.find(
                    (center: any) => center.value === centerId
                  );
                  return (
                    <div key={centerId} className="flex items-center justify-between gap-4">
                      <div className="flex-1 flex items-center gap-2">
                        <span className="w-[9px] h-[9px] rounded-full" style={{ background: centerData?.color }} />
                        <p className="flex-1 text-sm font-medium leading-[100%]">{centerData?.title || ''}</p>
                      </div>
                      <InputField
                        name={`${centerId}`}
                        placeholder="Amount"
                        register={register}
                        error={touchedFields.has(centerId) ? errors[`${centerId}`] : undefined}
                        onBlur={() => handleFieldBlur(centerId, 'departmentsAC')}
                        size="sm"
                        type="number"
                        className="w-[125px]"
                        icon={euroIcon}
                        min={0}
                        max={centerData?.max}
                      />
                    </div>
                  );
                })}

                {touchedSections.has('departmentsAC') && !isEqual.departmentsAC && (
                  <div className="text-red-500 text-sm">
                    Invoice allocation must have a value equal to the Invoice amount (€{allocationAmount})
                  </div>
                )}
              </div>
            )}
        </div>
      )}

      {accountingCentersOptions.productAndServices.length > 0 && (
        <div className="card space-y-4">
          <p className="font-medium text-base">Product and Services Accounting Center</p>
          <SelectField
            name="product_and_services_accounting_center"
            placeholder="Select accounting center"
            options={accountingCentersOptions.productAndServices}
            register={register}
            value={watch('product_and_services_accounting_center')}
            error={errors.product_and_services_accounting_center}
            size="sm"
            multiple
            title="accounting centers"
          />

          {watch('product_and_services_accounting_center') &&
            Array.isArray(watch('product_and_services_accounting_center')) &&
            watch('product_and_services_accounting_center').length > 0 && (
              <div className="space-y-4">
                <AllocationBar
                  items={(watch('product_and_services_accounting_center') || []).map((centerId: any) => {
                    const centerData = accountingCentersOptions.productAndServices.find(
                      (center: any) => center.value === centerId
                    );
                    return {
                      value: watch(`${centerId}`) || 0,
                      label: '',
                      color: centerData?.color || ''
                    };
                  })}
                  maxAllowedTotal={allocationAmount}
                  height={22}
                  borderRadius={12}
                  isOverAllocated={touchedSections.has('productsAndServicesAC') && !isEqual.productsAndServicesAC}
                />

                {watch('product_and_services_accounting_center').map((centerId: any) => {
                  const centerData = accountingCentersOptions.productAndServices.find(
                    (center: any) => center.value === centerId
                  );
                  return (
                    <div key={centerId} className="flex items-center justify-between gap-4">
                      <div className="flex-1 flex items-center gap-2">
                        <span className="w-[9px] h-[9px] rounded-full" style={{ background: centerData?.color }} />
                        <p className="flex-1 text-sm font-medium leading-[100%]">{centerData?.title || ''}</p>
                      </div>
                      <InputField
                        name={`${centerId}`}
                        placeholder="Amount"
                        register={register}
                        error={touchedFields.has(centerId) ? errors[`${centerId}`] : undefined}
                        onBlur={() => handleFieldBlur(centerId, 'productsAndServicesAC')}
                        size="sm"
                        type="number"
                        className="w-[125px]"
                        icon={euroIcon}
                        min={0}
                        max={centerData?.max}
                      />
                    </div>
                  );
                })}

                {touchedSections.has('productsAndServicesAC') && !isEqual.productsAndServicesAC && (
                  <div className="text-red-500 text-sm">
                    Invoice allocation must have a value equal to the Invoice amount (€{allocationAmount})
                  </div>
                )}
              </div>
            )}
        </div>
      )}

      {accountingCentersOptions.project.length > 0 && (
        <div className="card space-y-4">
          <p className="font-medium text-base">Project Accounting Center</p>
          <SelectField
            name="project_accounting_center"
            placeholder="Select accounting center"
            options={accountingCentersOptions.project}
            register={register}
            value={watch('project_accounting_center')}
            error={errors.project_accounting_center}
            size="sm"
            multiple
            title="accounting centers"
          />

          {watch('project_accounting_center') &&
            Array.isArray(watch('project_accounting_center')) &&
            watch('project_accounting_center').length > 0 && (
              <div className="space-y-4">
                <AllocationBar
                  items={(watch('project_accounting_center') || []).map((centerId: any) => {
                    const centerData = accountingCentersOptions.project.find(
                      (center: any) => center.value === centerId
                    );
                    return {
                      value: watch(`${centerId}`) || 0,
                      label: '',
                      color: centerData?.color || ''
                    };
                  })}
                  maxAllowedTotal={allocationAmount}
                  height={22}
                  borderRadius={12}
                  isOverAllocated={touchedSections.has('projectAC') && !isEqual.projectAC}
                />

                {(watch('project_accounting_center') || []).map((centerId: any) => {
                  const centerData = accountingCentersOptions.project.find((center: any) => center.value === centerId);
                  return (
                    <div key={centerId} className="flex items-center justify-between gap-4">
                      <div className="flex-1 flex items-center gap-2">
                        <span className="w-[9px] h-[9px] rounded-full" style={{ background: centerData?.color }} />
                        <p className="flex-1 text-sm font-medium leading-[100%]">{centerData?.title || ''}</p>
                      </div>
                      <InputField
                        name={`${centerId}`}
                        placeholder="Amount"
                        register={register}
                        error={touchedFields.has(centerId) ? errors[`${centerId}`] : undefined}
                        onBlur={() => handleFieldBlur(centerId, 'projectAC')}
                        size="sm"
                        type="number"
                        className="w-[125px]"
                        icon={euroIcon}
                        min={0}
                        max={centerData?.max}
                      />
                    </div>
                  );
                })}

                {touchedSections.has('projectAC') && !isEqual.projectAC && (
                  <div className="text-red-500 text-sm">
                    Invoice allocation must have a value equal to the Invoice amount (€{allocationAmount})
                  </div>
                )}
              </div>
            )}
        </div>
      )}

      {accountingCentersOptions.contract.length > 0 && (
        <div className="card space-y-4">
          <p className="font-medium text-base">Contract Accounting Center</p>
          <SelectField
            name="contract_accounting_center"
            placeholder="Select accounting center"
            options={accountingCentersOptions.contract}
            register={register}
            value={watch('contract_accounting_center')}
            error={errors.contract_accounting_center}
            size="sm"
            multiple
            title="accounting centers"
          />

          {watch('contract_accounting_center') &&
            Array.isArray(watch('contract_accounting_center')) &&
            watch('contract_accounting_center').length > 0 && (
              <div className="space-y-4">
                <AllocationBar
                  items={(watch('contract_accounting_center') || []).map((centerId: any) => {
                    const centerData = accountingCentersOptions.contract.find(
                      (center: any) => center.value === centerId
                    );
                    return {
                      value: watch(`${centerId}`) || 0,
                      label: '',
                      color: centerData?.color || ''
                    };
                  })}
                  maxAllowedTotal={allocationAmount}
                  height={22}
                  borderRadius={12}
                  isOverAllocated={touchedSections.has('contractAC') && !isEqual.contractAC}
                />

                {(watch('contract_accounting_center') || []).map((centerId: any) => {
                  const centerData = accountingCentersOptions.contract.find((center: any) => center.value === centerId);
                  return (
                    <div key={centerId} className="flex items-center justify-between gap-4">
                      <div className="flex-1 flex items-center gap-2">
                        <span className="w-[9px] h-[9px] rounded-full" style={{ background: centerData?.color }} />
                        <p className="flex-1 text-sm font-medium leading-[100%]">{centerData?.title || ''}</p>
                      </div>
                      <InputField
                        name={`${centerId}`}
                        placeholder="Amount"
                        register={register}
                        error={touchedFields.has(centerId) ? errors[`${centerId}`] : undefined}
                        onBlur={() => handleFieldBlur(centerId, 'contractAC')}
                        size="sm"
                        type="number"
                        className="w-[125px]"
                        icon={euroIcon}
                        min={0}
                        max={centerData?.max}
                      />
                    </div>
                  );
                })}

                {touchedSections.has('contractAC') && !isEqual.contractAC && (
                  <div className="text-red-500 text-sm">
                    Invoice allocation must have a value equal to the Invoice amount (€{allocationAmount})
                  </div>
                )}
              </div>
            )}
        </div>
      )}

      <div className="card space-y-4">
        <p className="font-medium text-base">
          {invoiceCategory === 'expense' ? 'Expense Category' : 'Revenue Category'}
        </p>
        <SelectField
          name={`invoice_categories`}
          placeholder={`Select ${invoiceCategory === 'expense' ? 'expense' : 'revenue'} category`}
          options={invoiceCategoriesOptions}
          value={watch(`invoice_categories`)}
          register={register}
          error={errors[`invoice_categories`]}
          size="sm"
          multiple
          title={`${invoiceCategory === 'expense' ? 'expense' : 'revenue'} categories`}
        />

        {watch(`invoice_categories`) &&
          Array.isArray(watch(`invoice_categories`)) &&
          watch(`invoice_categories`).length > 0 && (
            <div className="space-y-4">
              <AllocationBar
                items={(watch(`invoice_categories`) || []).map((centerId: any) => {
                  const centerData = invoiceCategoriesOptions.find((center: any) => center.value === centerId);
                  return {
                    value: watch(`${centerId}`) || 0,
                    label: '',
                    color: centerData?.color || ''
                  };
                })}
                maxAllowedTotal={allocationAmount}
                height={22}
                borderRadius={12}
                isOverAllocated={touchedSections.has('invoiceCategory') && !isEqual.invoiceCategory}
              />

              {(watch(`invoice_categories`) || []).map((centerId: any) => {
                const centerData = invoiceCategoriesOptions.find((center: any) => center.value === centerId);
                return (
                  <div key={centerId} className="flex items-center justify-between gap-4">
                    <div className="flex-1 flex items-center gap-2">
                      <span className="w-[9px] h-[9px] rounded-full" style={{ background: centerData?.color }} />
                      <p className="flex-1 text-sm font-medium leading-[100%]">{centerData?.title || ''}</p>
                    </div>
                    <InputField
                      name={`${centerId}`}
                      placeholder="Amount"
                      register={register}
                      error={touchedFields.has(centerId) ? errors[`${centerId}`] : undefined}
                      onBlur={() => handleFieldBlur(centerId, 'invoiceCategory')}
                      size="sm"
                      type="number"
                      className="w-[125px]"
                      icon={euroIcon}
                      min={0}
                      max={centerData?.max}
                    />
                  </div>
                );
              })}

              {touchedSections.has('invoiceCategory') && !isEqual.invoiceCategory && (
                <div className="text-red-500 text-sm">
                  Invoice allocation must have a value equal to the Invoice amount (€{allocationAmount})
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default CreateInvoiceAllocation;
